import { DocScanConfidence, DocScanTextValues, GetFarmDocScanFieldResponse } from "models";

import { EvidenceTone } from "pages/admin/verifications/components/automations/types";
import { formatSizeVariance } from "pages/admin/verifications/utils/docScanEvidence";

const EMPTY_VALUE = "-";

type DocScanIdentifierKey = keyof GetFarmDocScanFieldResponse["identifiers"];

export type DocScanComparisonRow = {
  key: string;
  label: string;
  documentValue: string;
  platformValue: string;
  // The platform value doc-scan compared against, set only when the field changed after the scan
  platformValueAtScan?: string;
  sizeVariance?: string;
  // Unset when the document agrees with the platform
  tone?: EvidenceTone;
};

export const DOC_SCAN_IDENTIFIERS: { key: DocScanIdentifierKey; label: string }[] = [
  { key: "fieldNumber", label: "Field no." },
  { key: "flik", label: "FLIK" },
  { key: "fieldName", label: "Field name" },
  { key: "size", label: "Size" },
  { key: "mainCrop", label: "Main crop" },
];

export const DOC_SCAN_CONFIDENCE_LABELS: Record<DocScanConfidence, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

export const DOC_SCAN_CONFIDENCE_TONES: Record<DocScanConfidence, EvidenceTone> = { high: "success", medium: "warning", low: "danger" };

export const countMatchingIdentifiers = (identifiers: GetFarmDocScanFieldResponse["identifiers"]) =>
  DOC_SCAN_IDENTIFIERS.filter(({ key }) => identifiers[key]).length;

const formatHectares = (size: number | null) => (size === null ? EMPTY_VALUE : `${size.toFixed(2)} ha`);

const getValueAtScan = (platformValue: string, platformValueAtScan: string) =>
  platformValueAtScan !== EMPTY_VALUE && platformValueAtScan !== platformValue ? platformValueAtScan : undefined;

const buildTextRow = (key: string, label: string, { doc, platform, platformAtScan }: DocScanTextValues, isMatch: boolean) => {
  const platformValue = platform || EMPTY_VALUE;
  return {
    key,
    label,
    documentValue: doc || EMPTY_VALUE,
    platformValue,
    platformValueAtScan: getValueAtScan(platformValue, platformAtScan || EMPTY_VALUE),
    tone: isMatch ? undefined : ("danger" as const),
  };
};

export const getDocScanComparisonRows = ({ comparison, identifiers, sizeVariancePct }: GetFarmDocScanFieldResponse): DocScanComparisonRow[] => {
  const { mainCrop, fieldSize, fieldNumber, fieldName, flik, requiresSplit } = comparison;
  const platformSize = formatHectares(fieldSize.platform);

  return [
    buildTextRow(
      "mainCrop",
      "Main crop",
      { doc: mainCrop.doc?.name ?? null, platform: mainCrop.platform?.name ?? null, platformAtScan: mainCrop.platformAtScan?.name ?? null },
      identifiers.mainCrop
    ),
    {
      key: "fieldSize",
      label: "Field size",
      documentValue: formatHectares(fieldSize.doc),
      platformValue: platformSize,
      platformValueAtScan: getValueAtScan(platformSize, formatHectares(fieldSize.platformAtScan)),
      sizeVariance: sizeVariancePct === null ? undefined : formatSizeVariance(sizeVariancePct),
      tone: identifiers.size ? undefined : "danger",
    },
    {
      key: "fieldSplit",
      label: "Field split",
      documentValue: requiresSplit ? "Split needed" : "No split",
      platformValue: "1 field",
      tone: requiresSplit ? "warning" : undefined,
    },
    buildTextRow("fieldNumber", "Field no.", fieldNumber, identifiers.fieldNumber),
    buildTextRow("fieldName", "Field name", fieldName, identifiers.fieldName),
    buildTextRow("flik", "FLIK", flik, identifiers.flik),
  ];
};

/** The shapes to compare on the map, or null for a document without shapes (doc-scan only matches PL parcels by shape). */
export const getDocScanBoundaryShapes = ({ overlap }: GetFarmDocScanFieldResponse) => {
  // A field drawn after the scan has no current shape yet, so the shape doc-scan compared against stands in.
  const platformShape = overlap?.platformShape ?? overlap?.platformShapeAtScan ?? null;
  if (!overlap || (!platformShape && !overlap.docShapes.length)) return null;
  return { platformShape, documentShapes: overlap.docShapes };
};
